import { useState } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { InputField } from "@/components/common/InputField";
import { MfaCodeInput } from "./MfaCodeInput";

export interface MfaMethods {
	totp: boolean;
	passkey: boolean;
	recoveryCode: boolean;
}

export interface MfaVerifier {
	totp: (code: string) => Promise<void>;
	recoveryCode: (code: string) => Promise<void>;
	/** 取り消されたら false。 */
	passkey: () => Promise<boolean>;
}

interface MfaVerifyFormProps {
	methods: MfaMethods;
	/** null は確認中。 */
	passkeyUsable: boolean | null;
	verifier: MfaVerifier;
	onVerified: () => void;
	/** 失敗をその場で出せないもの (ログイン待ちの期限切れなど) を親に渡す。 */
	onFatalError?: (err: unknown) => boolean;
}

/** 登録済みの手段で本人確認する。サインインの 2 段目と、設定を変える前の確認し直しで使う。 */
export function MfaVerifyForm({
	methods,
	passkeyUsable,
	verifier,
	onVerified,
	onFatalError,
}: MfaVerifyFormProps) {
	// null はコード欄を出していない状態。パスキーを使えるときは、それを主に使わせるため、押されるまでコード欄を隠す。
	const [chosenMethod, setMethod] = useState<"totp" | "recovery" | null>(null);
	const method =
		chosenMethod ?? (passkeyUsable === false ? (methods.totp ? "totp" : "recovery") : null);
	const showCodeForm = method !== null && (methods.totp || methods.recoveryCode);
	const [code, setCode] = useState("");
	const [busy, setBusy] = useState<"code" | "passkey" | null>(null);
	const [error, setError] = useState<string | null>(null);

	async function run(kind: "code" | "passkey", action: () => Promise<boolean>) {
		setBusy(kind);
		setError(null);
		try {
			if (await action()) onVerified();
		} catch (err) {
			if (onFatalError?.(err)) return;
			setError(err instanceof Error ? err.message : "確認できませんでした。");
		} finally {
			setBusy(null);
		}
	}

	function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		void run("code", async () => {
			await (method === "totp" ? verifier.totp(code) : verifier.recoveryCode(code));
			return true;
		});
	}

	function switchMethod(next: "totp" | "recovery") {
		setMethod(next);
		setCode("");
		setError(null);
	}

	return (
		<div data-testid="mfa-verify" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			{passkeyUsable && (
				<ConfirmButton
					label="パスキーで確認"
					loading={busy === "passkey"}
					disabled={busy !== null}
					onClick={() => void run("passkey", verifier.passkey)}
					data-testid="mfa-verify-passkey"
				/>
			)}
			{showCodeForm && (
				<form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
					<InputField
						label={method === "totp" ? "認証アプリのコード" : "リカバリーコード"}
						error={error ?? undefined}
					>
						<MfaCodeInput
							key={method}
							value={code}
							onChange={setCode}
							kind={method}
							autoFocus={!passkeyUsable || chosenMethod !== null}
							testId="mfa-verify-code"
						/>
					</InputField>
					<ConfirmButton
						type="submit"
						label="確認"
						variant={passkeyUsable ? "secondary" : "primary"}
						loading={busy === "code"}
						disabled={busy !== null || code.trim().length === 0}
						data-testid="mfa-verify-submit"
					/>
				</form>
			)}
			{!showCodeForm && error && (
				<p style={{ margin: 0, fontSize: "var(--text-caption)", color: "var(--danger)" }}>
					{error}
				</p>
			)}
			{methods.totp && method !== "totp" && (
				<LinkButton label="認証アプリのコードを使う" onClick={() => switchMethod("totp")} />
			)}
			{methods.recoveryCode && method !== "recovery" && (
				<LinkButton label="リカバリーコードを使う" onClick={() => switchMethod("recovery")} />
			)}
			<p
				style={{
					margin: 0,
					fontSize: "var(--text-caption)",
					color: "var(--fg-3)",
					lineHeight: "var(--leading-ui)",
				}}
			>
				どの手段も使えない場合は、組織の管理者に多要素認証のリセットを依頼してください。
			</p>
		</div>
	);
}

function LinkButton({ label, onClick }: { label: string; onClick: () => void }) {
	return (
		<button
			type="button"
			onClick={onClick}
			style={{
				alignSelf: "flex-start",
				padding: 0,
				border: 0,
				background: "transparent",
				fontFamily: "inherit",
				fontSize: "var(--text-caption)",
				color: "var(--accent)",
				cursor: "pointer",
			}}
		>
			{label}
		</button>
	);
}
